'use client';

import type { Address, PublicClient } from 'viem';
import { erc20Abi, formatUnits } from 'viem';
import { usePublicClient } from 'wagmi';

import { ETHEREUM_TOKENS, type TokenInfo } from '@shaojiang/config/tokens';
import { useQuery } from '@tanstack/react-query';

export interface TokenBalance extends TokenInfo {
  balance: bigint;
  balanceFormatted: string;
}

/** Read one ERC-20 balance straight from the contract; a failing token counts as 0. */
async function getTokenBalance(
  publicClient: PublicClient,
  account: Address,
  tokenAddress: Address,
): Promise<bigint> {
  try {
    return await publicClient.readContract({
      abi: erc20Abi,
      address: tokenAddress,
      functionName: 'balanceOf',
      args: [account],
    });
  } catch (error) {
    console.error(`Error fetching balance for ${tokenAddress}:`, error);
    return BigInt(0);
  }
}

/**
 * Non-zero ERC-20 balances (from the known token list) for an address, via react-query.
 * Reads contracts with viem, no external indexer API.
 */
export function useTokenBalances(address?: Address, chainId: number = 1) {
  const publicClient = usePublicClient({ chainId });

  const query = useQuery({
    queryKey: ['token-balances', chainId, address],
    enabled: !!address && !!publicClient,
    staleTime: 60_000,
    queryFn: async (): Promise<TokenBalance[]> => {
      const balances = await Promise.all(
        ETHEREUM_TOKENS.map(async (token) => {
          const balance = await getTokenBalance(
            publicClient as PublicClient,
            address as Address,
            token.address,
          );
          return { ...token, balance, balanceFormatted: formatUnits(balance, token.decimals) };
        }),
      );
      return balances.filter((token) => token.balance > BigInt(0));
    },
  });

  return {
    tokens: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}
